import {
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  toast,
} from '@aus-platform/design-system';
import { isEmpty, isNil, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  useInitialize2FASetup,
  useVerifySetup2FA,
} from 'shared/api/auth/auth-api';
import { BackupCodes, Callout, SetupVerification } from 'shared/components';
import {
  getLockoutErrorMessage,
  TwoFactorErrorMessages,
} from 'shared/constants';
import { ErrorSlugs } from 'shared/enums';
import { CustomDate } from 'shared/utils';

// Types.
type TwoFactorEnforcementModalProps = {
  gracePeriodDeadline: string | null;
  onClose: VoidFunction;
};

// Enums.
enum TwoFactorEnforcementView {
  Required = 'REQUIRED',
  Setup = 'SETUP',
  BackupCodes = 'BACKUP_CODES',
}

// Constants.
export const ENFORCEMENT_MODAL_KEY = 'is2FAEnforcementModalShown';
const OTP_CELL_COUNT = 6;

export const TwoFactorEnforcementModal: React.FC<
  TwoFactorEnforcementModalProps
> = ({ gracePeriodDeadline, onClose }) => {
  // States.
  const [currentView, setCurrentView] = useState<TwoFactorEnforcementView>(
    TwoFactorEnforcementView.Required,
  );
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState<string>();

  // Apis.
  const {
    mutate: initialize2FASetup,
    data: initializeSetupData,
    isSuccess: isInitializeSetupSuccess,
    isPending: isInitializingSetup,
    isError: isInitializeSetupError,
    error: initializeSetupError,
  } = useInitialize2FASetup();

  const {
    mutate: verifySetup,
    data: verificationResponse,
    isSuccess: isVerificationSuccess,
    isPending: isVerifying,
    isError: isVerificationError,
    error: verificationError,
  } = useVerifySetup2FA();

  // Variables.
  const isAccountLocked = verificationError?.meta?.status_code === 429;

  // Effects.
  useEffect(() => {
    if (isInitializeSetupSuccess) {
      setCurrentView(TwoFactorEnforcementView.Setup);
    }
  }, [isInitializeSetupSuccess]);

  useEffect(() => {
    if (isVerificationSuccess) {
      setCurrentView(TwoFactorEnforcementView.BackupCodes);
    }
  }, [isVerificationSuccess]);

  useEffect(() => {
    if (!isInitializeSetupError) {
      return;
    }

    if (
      initializeSetupError?.meta?.slug === ErrorSlugs.TwoFADeviceAlreadySetup
    ) {
      onClose();
    }
  }, [isInitializeSetupError, initializeSetupError]);

  useEffect(() => {
    if (!isVerificationError) {
      return;
    }

    setOtpValue('');

    if (verificationError?.meta?.slug === ErrorSlugs.InvalidCode) {
      setError(TwoFactorErrorMessages.InvalidAuthenticatorCode);
      return;
    }

    if (isAccountLocked) {
      const remainingMinutes = new CustomDate(
        verificationError?.data?.lockout_expires_at,
      ).getRemainingMinutes();

      setError(getLockoutErrorMessage(remainingMinutes ?? 0));
    }
  }, [isVerificationError, verificationError]);

  // Handlers.
  const handleConfigure = () => {
    initialize2FASetup({});
  };

  const handleSetUpLater = () => {
    onClose();
  };

  const handleVerifyAndContinue = () => {
    verifySetup({ code: otpValue });
  };

  const handleDone = () => {
    localStorage.removeItem(ENFORCEMENT_MODAL_KEY);
    toast.success('Two-Factor Authentication configured successfully.');
    onClose();
  };

  const renderTwoFactorRequiredView = () => {
    const hoursRemaining = new CustomDate(
      gracePeriodDeadline,
    )?.getRemainingHours();

    return (
      <>
        <div className="two-factor-enforcement-modal__message">
          Two-factor authentication is now enforced for your organisation. You
          can set it up now, or complete the setup later from
          <strong> Settings → Security</strong>.
        </div>

        <FormMessage
          showIcon
          variant={FormMessageVariant.Warning}
          message={`If two-factor authentication is not set up within ${hoursRemaining} ${
            hoursRemaining === 1 ? 'hour' : 'hours'
          }, you will be automatically logged out of your account.`}
        />
      </>
    );
  };

  const renderConfigure2FAView = () => {
    const setupData = initializeSetupData?.data;

    if (isNil(setupData)) {
      return null;
    }

    return (
      <>
        <div className="two-factor-enforcement-modal__instruction">
          Install an authenticator app such as Google Authenticator, Microsoft
          Authenticator, or Authy and use it to scan the QR code.
        </div>

        <SetupVerification
          otpValue={otpValue}
          onOtpChange={setOtpValue}
          qrCodeUrl={setupData.qrCodeUrl}
          secretKey={setupData.manualEntryKey}
          error={error}
          disabled={isVerifying || isAccountLocked}
        />
      </>
    );
  };

  const renderBackupCodes = () => {
    const backupCodes = verificationResponse?.data?.backupCodes;

    if (isUndefined(backupCodes) || isEmpty(backupCodes)) {
      return null;
    }

    return (
      <>
        <div className="two-factor-enforcement-modal__message">
          Use these codes to access your account if you lose your device. Each
          code can only be used once. If you regenerate new codes, your old
          codes will no longer work.
        </div>

        <BackupCodes codes={backupCodes} />

        <Callout
          variant={FormMessageVariant.Warning}
          title="Keep these codes secure"
          items={[
            'Store them in a password manager or any secure location.',
            'Each code can be used only once.',
            'If you lose access to your authenticator app, these codes are the only way to sign in.',
          ]}
        />
      </>
    );
  };

  const renderModalBody = () => {
    switch (currentView) {
      case TwoFactorEnforcementView.Required:
        return renderTwoFactorRequiredView();

      case TwoFactorEnforcementView.Setup:
        return renderConfigure2FAView();

      case TwoFactorEnforcementView.BackupCodes:
        return renderBackupCodes();

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentView) {
      case TwoFactorEnforcementView.Required:
        return (
          <>
            <Button
              variant={ButtonVariant.Secondary}
              onClick={handleSetUpLater}
            >
              Set Up Later
            </Button>
            <Button onClick={handleConfigure} isLoading={isInitializingSetup}>
              Configure 2FA
            </Button>
          </>
        );

      case TwoFactorEnforcementView.Setup:
        return (
          <Button
            onClick={handleVerifyAndContinue}
            disabled={otpValue.length !== OTP_CELL_COUNT}
            isLoading={isVerifying}
          >
            Verify and Continue
          </Button>
        );

      case TwoFactorEnforcementView.BackupCodes:
        return <Button onClick={handleDone}>Done</Button>;

      default:
        return null;
    }
  };

  return (
    <Modal
      className="two-factor-enforcement-modal"
      show
      centered
      backdrop="static"
      keyboard={false}
      onHide={onClose}
    >
      <Modal.Header closeButton>
        {currentView === TwoFactorEnforcementView.Required
          ? 'Two-Factor Authentication Required'
          : 'Configure Two-Factor Authentication'}
      </Modal.Header>

      <Modal.Body>{renderModalBody()}</Modal.Body>

      <Modal.Footer>{renderFooter()}</Modal.Footer>
    </Modal>
  );
};
